import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { SplatMesh, SparkRenderer } from '@sparkjsdev/spark'
import { SPARK_TRAINING_BLUR } from '../ogs/render/sparkBlur.js'
import { estimateSplatFloorY } from '../lib/splatFloorHeight'
import * as splatCache from '../lib/splatCache'
import { plyTruncationReason, PLY_HEADER_SCAN_BYTES } from '../lib/plyIntegrity'
import { useLiveTune } from '../hooks/useLiveTune'
import { cameraSeeThrough } from '../lib/cameraSeeThrough'
import { cameraController } from '../lib/cameraController'

/**
 * Gaussian Splat environment (via Spark 2.0).
 *
 * Spark needs two things in the scene:
 *   1. A SparkRenderer instance, attached to the THREE.WebGLRenderer — this
 *      owns the off-main-loop sorting / LoD / GPU work for splat rendering.
 *      Without it, SplatMesh is invisible.
 *   2. One or more SplatMesh instances loaded from PLY/SPZ/SPLAT.
 *
 * Both are regular THREE.Object3D / THREE.Mesh subclasses, so we drop them
 * in as `<primitive>` children.
 *
 * Scale/pose is capture-dependent. On load we log the bounding box so the
 * pose can be dialed in via props (or the `?sx=`, `?sy=`, `?sz=`, `?ss=`,
 * `?sry=` URL params honored by `<Scene>`).
 */
export default function SplatEnvironment({
  url,
  version, // cache-busting token (environment_assets updatedAt); changes → re-fetch
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  onLoad,
  onError,
  onFloorY, // called with world-space Y of detected floor around [0,0] once
}) {
  const gl = useThree(s => s.gl)

  // Expose the live renderer + THREE module on window so the offline bake
  // tool's page.evaluate can build a CubeTexture → equirect render pass
  // without re-importing three inside the page context.
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.__GL__ = gl
    window.__THREE__ = THREE
    return () => {
      if (window.__GL__ === gl) delete window.__GL__
      if (window.__THREE__ === THREE) delete window.__THREE__
    }
  }, [gl])

  const sparkRenderer = useMemo(() => {
    if (!gl) return null
    try {
      return new SparkRenderer({ renderer: gl, ...SPARK_TRAINING_BLUR })
    } catch (err) {
      console.error('[SplatEnvironment] SparkRenderer init failed:', err)
      onError?.(err)
      return null
    }
  }, [gl]) // eslint-disable-line react-hooks/exhaustive-deps

  const [mesh, setMesh] = useState(null)
  const disposedRef = useRef(false)

  // See-through: fade splats between camera and head. Config comes
  // from the blueprint-driven liveTuneStore; the Spark worldModifier lives in
  // cameraSeeThrough. Re-install when the mesh or any config field changes.
  const seeThrough = useLiveTune().camera.seeThrough
  useEffect(() => {
    if (!mesh) return
    cameraSeeThrough.install(mesh, seeThrough)
    return () => cameraSeeThrough.uninstall()
  }, [mesh, seeThrough.enabled, seeThrough.auto, seeThrough.radius, seeThrough.fade])

  // Feed the modifier camera + head world positions each frame. The controller
  // owns the frame timing; we only bridge it to the Spark uniforms so the dyno
  // never leaks into cameraController.
  useEffect(() => {
    cameraController.setSeeThroughSink((camPos, headPos, strength) => cameraSeeThrough.update(camPos, headPos, strength))
    return () => cameraController.setSeeThroughSink(null)
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__SPLAT_LOADED__ = false
      window.__SPLAT_LOAD_PROGRESS__ = 0
      window.__SPLAT_DECODE_PROGRESS__ = 0   // 0..1 parse/decode %, or -1 if total unknown
      window.__SPLAT_LOAD_ERROR__ = null
      window.__SPLAT_BYTES_RECEIVED__ = 0
      window.__SPLAT_BYTES_TOTAL__ = 0
      // Cleared by default — only set true once we actually begin a fetch
      // below. Without this, refreshes with a persisted splatUrl would
      // show the loading overlay forever (the URL is in liveTuneStore but
      // SplatEnvironment isn't mounted in edit mode → no fetch ever starts).
      window.__SPLAT_LOAD_STARTED__ = false
    }
    disposedRef.current = false
    if (!url) {
      if (typeof window !== 'undefined') window.__SPLAT_MESH__ = null
      return
    }
    if (typeof window !== 'undefined') window.__SPLAT_LOAD_STARTED__ = true
    console.log('[SplatEnvironment] loading', url)

    // Pre-fetch the splat via streaming fetch so we can drive a real
    // progress %. SparkRenderer's SplatMesh({ url }) doesn't expose a
    // download-progress callback, so we download into memory ourselves
    // and hand SplatMesh a blob: URL (instant local fetch). The browser
    // briefly holds the file twice in RAM (our buf + Spark's parse), but
    // for a 125MB scan that's a fine trade for actual UX feedback.
    let created = null
    let blobUrl = null
    const aborter = new AbortController()

    ;(async () => {
      let downloadedUrl
      let fromCache = false
      try {
        // Persistent cache first — a hit skips the multi-100MB download.
        const cachedUrl = await splatCache.matchBlobUrl(url, version)
        if (disposedRef.current) { if (cachedUrl) URL.revokeObjectURL(cachedUrl); return }
        if (cachedUrl) {
          console.log('[SplatEnvironment] cache hit', url)
          fromCache = true
          blobUrl = cachedUrl
          downloadedUrl = cachedUrl
          if (typeof window !== 'undefined') {
            window.__SPLAT_LOAD_PROGRESS__ = 1
            window.__SPLAT_FROM_CACHE__ = true
          }
        } else {
        if (typeof window !== 'undefined') window.__SPLAT_FROM_CACHE__ = false
        const res = await fetch(url, { signal: aborter.signal })
        if (!res.ok) throw new Error(`fetch splat ${res.status} ${res.statusText}`)
        // A missing file behind the SPA dev server returns index.html with a
        // 200 + text/html. Without this guard we'd cache the HTML and hand it
        // to Spark, which rejects it deep in a worker ("Unknown file type")
        // where the error never reaches onError — the overlay then hangs at
        // "decoding 100%". Fail loudly here instead (CLAUDE.md: no silent
        // fallbacks to fake data).
        const ctype = res.headers.get('content-type') ?? ''
        if (ctype.includes('text/html')) {
          throw new Error(`expected a splat at ${url} but the server returned HTML — the file is missing or the URL is wrong`)
        }
        const total = Number(res.headers.get('content-length') ?? 0)
        const reader = res.body?.getReader()
        if (reader) {
          const chunks = []
          let received = 0
          while (true) {
            const { done, value } = await reader.read()
            if (done) break
            chunks.push(value)
            received += value.length
            if (typeof window !== 'undefined') {
              window.__SPLAT_LOAD_PROGRESS__ = total > 0 ? Math.min(1, received / total) : -1
              window.__SPLAT_BYTES_RECEIVED__ = received
              window.__SPLAT_BYTES_TOTAL__ = total
            }
            if (disposedRef.current) return
          }
          const blob = new Blob(chunks, { type: 'application/octet-stream' })
          // Belt-and-suspenders: some servers serve the SPA fallback without a
          // text/html content-type. Sniff the first byte — '<' means HTML
          // (<!DOCTYPE / <html), never a valid PLY/SPZ/SPLAT/KSPLAT.
          const head = new Uint8Array(await blob.slice(0, PLY_HEADER_SCAN_BYTES).arrayBuffer())
          if (head[0] === 0x3c /* '<' */) {
            throw new Error(`expected a splat at ${url} but got HTML — the file is missing or the URL is wrong`)
          }
          // Verify BEFORE caching — a short body must never become a cache
          // entry. Two independent witnesses, either sufficient:
          //  1. Content-Length, when the server sent one. (The env-file route
          //     currently streams chunked without it, but a presigned/direct
          //     fetch would have it — check what we actually got.)
          //  2. A binary PLY header's own declared size (self-describing, the
          //     only witness on a chunked stream an intermediary cut cleanly).
          // Loud failure per CLAUDE.md: throw to the error overlay, never
          // cache-and-hope. (Observed failure this guards: a cached 126 MB
          // copy of a 568 MB Startup_Level.ply hanging Spark's worker.)
          if (total > 0 && received < total) {
            throw new Error(`splat download truncated: got ${received} of ${total} bytes — check the connection and retry`)
          }
          const shortPly = plyTruncationReason(head, blob.size)
          if (shortPly) {
            throw new Error(`splat download incomplete (${shortPly}) — check the connection and retry`)
          }
          await splatCache.store(url, version, blob) // best-effort; never throws
          blobUrl = URL.createObjectURL(blob)
          downloadedUrl = blobUrl
        } else {
          // No streaming reader — fall back to the original URL (Spark
          // will fetch it again, but at least it won't break).
          downloadedUrl = url
        }
        }
      } catch (err) {
        if (disposedRef.current || aborter.signal.aborted) return
        console.error('[SplatEnvironment] pre-fetch failed:', err)
        if (typeof window !== 'undefined') window.__SPLAT_LOAD_ERROR__ = String(err?.message ?? err)
        onError?.(err)
        return
      }
      if (disposedRef.current) return
      if (typeof window !== 'undefined') window.__SPLAT_LOAD_PROGRESS__ = 1

      try {
        created = new SplatMesh({
          url: downloadedUrl,
          // Real decode/parse progress from Spark's worker. Download (above)
          // is already complete by here, so this drives the post-download
          // "decoding splats" bar instead of a blind elapsed counter.
          onProgress: (e) => {
            if (typeof window === 'undefined') return
            window.__SPLAT_DECODE_PROGRESS__ =
              e && e.lengthComputable && e.total > 0 ? Math.min(1, e.loaded / e.total) : -1
          },
          onLoad: (m) => {
            if (disposedRef.current) return
            try {
              const box = m.getBoundingBox?.(true)
              if (box) {
                const size = box.getSize(new m.position.constructor())
                const center = box.getCenter(new m.position.constructor())
                console.log('[SplatEnvironment] loaded', url,
                  `size=[${size.x.toFixed(2)}, ${size.y.toFixed(2)}, ${size.z.toFixed(2)}]`,
                  `center=[${center.x.toFixed(2)}, ${center.y.toFixed(2)}, ${center.z.toFixed(2)}]`)
              }
            } catch { /* ignore */ }
            window.__SPLAT_MESH__ = m
            window.__SPLAT_LOADED__ = true
            requestAnimationFrame(() => {
              if (disposedRef.current) return
              const y = estimateSplatFloorY(m, { targetX: 0, targetZ: 0, radius: 1.5 })
              if (y != null) {
                console.log(`[SplatEnvironment] floor estimated at y=${y.toFixed(3)}`)
                onFloorY?.(y)
              } else {
                console.warn('[SplatEnvironment] not enough splats under the character to estimate floor')
              }
            })
            onLoad?.(m)
          },
        })
        // Spark's parse/decode runs async in a worker; a bad file (truncated
        // PLY, unknown format) REJECTS `initialized` rather than calling
        // onLoad — and with no catch that rejection is an Uncaught (in
        // promise) while the overlay hangs at "decoding 100%" forever
        // (observed: `Expected 2406393 splats, got 535587` on a truncated
        // cached Startup_Level.ply). Surface it to the same error path as a
        // failed fetch, and if the bad bytes came from the persistent cache,
        // evict them so a reload re-downloads instead of replaying the
        // poison.
        created.initialized?.catch((err) => {
          if (disposedRef.current || aborter.signal.aborted) return
          console.error('[SplatEnvironment] splat decode failed:', err)
          if (typeof window !== 'undefined') window.__SPLAT_LOAD_ERROR__ = String(err?.message ?? err)
          if (fromCache) {
            console.warn('[SplatEnvironment] decode failed on a cached copy — evicting so the next load re-downloads', url)
            splatCache.evict(url) // best-effort; never throws
          }
          onError?.(err)
        })
        setMesh(created)
      } catch (err) {
        console.error('[SplatEnvironment] SplatMesh construct failed:', err)
        if (typeof window !== 'undefined') window.__SPLAT_LOAD_ERROR__ = String(err?.message ?? err)
        onError?.(err)
      }
    })()

    return () => {
      disposedRef.current = true
      aborter.abort()
      setMesh(null)
      if (typeof window !== 'undefined') {
        window.__SPLAT_LOAD_STARTED__ = false
        // Also retract the "there is a loaded splat" claim. These outlived the
        // mesh: __SPLAT_LOADED__ stayed true and __SPLAT_MESH__ kept handing out
        // a DISPOSED mesh after unmount, so any reader (the loading overlay, the
        // console, a test) was told a splat is in the scene when none is. Matters
        // now that a splat can be unmounted while the session continues (BACKDROP
        // → hide environment), not just at teardown.
        window.__SPLAT_LOADED__ = false
        window.__SPLAT_MESH__ = null
      }
      if (created?.dispose) {
        try { created.dispose() } catch { /* ignore */ }
      }
      if (blobUrl) {
        try { URL.revokeObjectURL(blobUrl) } catch { /* ignore */ }
      }
    }
  }, [url, version]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {sparkRenderer && <primitive object={sparkRenderer} />}
      {mesh && (
        <primitive
          object={mesh}
          position={position}
          rotation={rotation}
          scale={scale}
          onUpdate={(o) => {
            if (typeof window !== 'undefined') {
              window.__SPLAT_POSE__ = {
                position: [o.position.x, o.position.y, o.position.z],
                rotation: [o.rotation.x, o.rotation.y, o.rotation.z],
                scale: o.scale.x,
              }
            }
          }}
        />
      )}
    </>
  )
}
